import fastSort from 'fast-sort';
import { createSelector } from 'reselect';
import { Application, ApplicationStatus, ApplicationsByStatus } from '../types/applications';
import { StateInterface } from '../types/states';
import { getIndexWithKey } from '../utils/arrays';
import { isSuperLikeActive } from '../utils/applications';
import { selectNestedListIdByName } from './lists';
const selectApplicationsDomain = (state: StateInterface) => state.applications;
const selectApplications = createSelector(selectApplicationsDomain, (applications) => applications.applications);
const selectApplicationsAsArray = createSelector(selectApplications, (applications) => Object.values(applications || {}) || []);
const selectApplicationsByApartmentId = (apartmentId: string) => createSelector(selectApplications, (applications) => {
    return applications
        ? (applications[apartmentId] as Application[]) || []
        : ([] as Application[]);
});
const selectApplicationByApartmentIdAndApplicationId = ({ apartmentId, applicationId }: {
    apartmentId: string;
    applicationId: string;
}): any => createSelector(selectApplicationsByApartmentId(apartmentId) as any, (applications: any) => applications ? applications[applicationId] || undefined : undefined);
const getApplicationsGroupedByStatus = (applications: Application[]) => applications.reduce<ApplicationsByStatus>((applicationsByStatus, application) => {
    const { status } = application;
    if (status === ApplicationStatus.created) {
        applicationsByStatus.created.push(application);
    }
    else if (status === ApplicationStatus.noAvailableTimes) {
        applicationsByStatus.noAvailableTimes.push(application);
    }
    else if (status === ApplicationStatus.notInterested) {
        applicationsByStatus.notInterested.push(application);
    }
    else if (status === ApplicationStatus.rejected) {
        applicationsByStatus.rejected.push(application);
    }
    else if (status === ApplicationStatus.accepted) {
        applicationsByStatus.accepted.push(application);
    }
    else if (status === ApplicationStatus.showingReserved) {
        applicationsByStatus.showingReserved.push(application);
    }
    else if (status === ApplicationStatus.wantsContract) {
        applicationsByStatus.wantsContract.push(application);
    }
    else if (status === ApplicationStatus.invitationsSent) {
        applicationsByStatus.invitationsSent.push(application);
    }
    else if (status === ApplicationStatus.tenant) {
        applicationsByStatus.tenant.push(application);
    }
    else {
        applicationsByStatus.other.push(application);
    }
    fastSort(applicationsByStatus.created).by([
        {
            desc: (app) => (isSuperLikeActive(app) ? 1 : 0)
        },
        {
            desc: (app) => app.createdAt
        }
    ]);
    return applicationsByStatus;
}, {
    created: [],
    rejected: [],
    accepted: [],
    showingReserved: [],
    noAvailableTimes: [],
    notInterested: [],
    wantsContract: [],
    invitationsSent: [],
    tenant: [],
    other: []
});
const selectApplicationsByStatus = (applications: Application[]) => applications.reduce<ApplicationsByStatus>((applicationsByStatus, application) => {
    const { status } = application;
    if (status === ApplicationStatus.created) {
        applicationsByStatus.created.push(application);
    }
    else if (status === ApplicationStatus.noAvailableTimes) {
        applicationsByStatus.noAvailableTimes.push(application);
    }
    else if (status === ApplicationStatus.notInterested) {
        applicationsByStatus.rejected.push(application);
    }
    else if (status === ApplicationStatus.rejected) {
        applicationsByStatus.rejected.push(application);
    }
    else if (status === ApplicationStatus.accepted) {
        applicationsByStatus.accepted.push(application);
    }
    else if (status === ApplicationStatus.showingReserved) {
        applicationsByStatus.showingReserved.push(application);
    }
    else if (status === ApplicationStatus.wantsContract) {
        applicationsByStatus.showingReserved.push(application);
    }
    else if (status === ApplicationStatus.invitationsSent) {
        applicationsByStatus.showingReserved.push(application);
    }
    else if (status === ApplicationStatus.tenant) {
        applicationsByStatus.showingReserved.push(application);
    }
    else {
        applicationsByStatus.other.push(application);
    }
    fastSort(applicationsByStatus.created).by([
        {
            desc: (app) => (isSuperLikeActive(app) ? 1 : 0)
        },
        {
            desc: (app) => app.createdAt
        }
    ]);
    return applicationsByStatus;
}, {
    created: [],
    rejected: [],
    accepted: [],
    showingReserved: [],
    noAvailableTimes: [],
    other: []
});
const getApplicationsNameIdPairs = (apartmentId: string, state: StateInterface) => {
    const applications = selectApplicationsByApartmentId(apartmentId)(state);
    return applications.map(({ applicationId, data: { name } }) => {
        return { id: applicationId, name: name };
    });
};
const selectNewApplications = (applications: Application[]) => applications.filter(({ status }) => status === ApplicationStatus.created);
const selectSelectedListApplication = (applications: Application[]) => createSelector(selectNestedListIdByName('applicationsList'), (applicationListId) => applications &&
    applications[getIndexWithKey(applications, 'applicationId', applicationListId)]);
const getSelectedListApplicationIndex = (applications: Application[]) => createSelector(selectNestedListIdByName('applicationsList'), (applicationListId) => getIndexWithKey(applications, 'applicationId', applicationListId));
const selectIsApplicationsLoading = createSelector(selectApplicationsDomain, (applications) => applications.isApplicationsLoading);
const selectCreateApplication = createSelector(selectApplicationsDomain, (applications) => applications.createApplication || {});
const selectSelectedApplications = createSelector(selectApplicationsDomain, (applications) => applications.selectedApplications);
const selectLeadApplication = createSelector(selectApplicationsDomain, (applications) => applications.getApartmentLead || {});
const selectConversation = createSelector(selectApplicationsDomain, (applications) => applications.getConversation || {});
export { selectApplications, selectApplicationsAsArray, selectApplicationsByApartmentId, selectApplicationByApartmentIdAndApplicationId, selectApplicationsByStatus, selectNewApplications, selectCreateApplication, selectIsApplicationsLoading, selectSelectedListApplication, getSelectedListApplicationIndex, selectSelectedApplications, getApplicationsNameIdPairs, getApplicationsGroupedByStatus, selectLeadApplication, selectConversation };
